import { MenuItem } from '@mui/material';
import RoundedSelect from 'common/components/RoundedSelect';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { transformFiltersToQuery } from 'redux/slices/utils/transformFilterToQuery';

const CustomPaginatedSelect = ({
    customMenuProps,
    id,
    type,
    onChange,
    selectAll,
    fullWidth,
    selectNone,
    size = 'small',
    update,
    organizationId,
    fetchAction,
    label,
    i18nKeys,
    queryParams = {},
    disabled,
    primaryId = 'id',
    items: itemsProp,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [hasMore, setHasMore] = useState(false);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [items, setItems] = useState(itemsProp || []);
    const [displayValue, setDisplayValue] = useState('all');
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [pending, setPending] = useState(false);
    const [lastQueryParams, setLastQueryParams] = useState(null);

    // const [currentSelectedItem, setCurrentSelectedItem] = useState(selectedById);

    useEffect(() => {
        setDisplayValue(id || 'all');
    }, [id]);

    useEffect(() => {
        if (itemsProp?.length) {
            setItems((currentItems) => {
                const existingIds = new Set(currentItems.map((item) => item[primaryId]));

                const filteredItemsProp = itemsProp.filter((item) => !existingIds.has(item[primaryId]));
                return [...filteredItemsProp, ...currentItems];
            });
        }
    }, [itemsProp, primaryId]);

    const extendedQueryParams = useMemo(
        () => ({
            ...queryParams,
            ...(search ? { search, name: search } : {}),
            ...(organizationId && { organizationId }),
            // ...(id ? { priorityIds: id } : {}),
        }),
        [queryParams, search, organizationId],
    );

    const shouldReset = useMemo(
        () =>
            (open && !lastQueryParams) ||
            (lastQueryParams && lastQueryParams !== transformFiltersToQuery(extendedQueryParams)),
        [open, lastQueryParams, extendedQueryParams],
    );

    const loadData = useCallback(() => {
        setPending(true);

        dispatch(
            fetchAction({
                queryParams: {
                    ...extendedQueryParams,
                    // ...((id && shouldReset) ? { priorityIds: id } : {}), // TODO: Add this back when the backend is fixed
                    page: shouldReset ? 1 : currentPage + 1,
                    limit: 50,
                },
                onSuccess: (data) => {
                    // setInitialLoadDone(true);
                    setLastQueryParams(transformFiltersToQuery(extendedQueryParams));
                    setHasMore(false);
                    setTotal(data?.total);
                    setCurrentPage(data?.currentPage);
                    setTotalPages(data?.totalPages);
                    setItems((prevItems) => {
                        // If shouldReset is true, only keep the selected item (if any)
                        const baseItems = shouldReset ? [] : prevItems;

                        // Filter out duplicates
                        const newItems = (data?.items || []).filter(
                            (item) => !baseItems.some((prevItem) => prevItem[primaryId] === item[primaryId]),
                        );

                        // Create the updated list
                        let updatedItems = [...baseItems, ...newItems];

                        // Make sure selected item is included if we have one
                        // if (currentSelectedItem && id && id !== 'all') {
                        //     if (!updatedItems.some((item) => item[primaryId] === currentSelectedItem?.[primaryId])) {
                        //         updatedItems = [currentSelectedItem, ...updatedItems];
                        //     }
                        // }

                        update?.(updatedItems);
                        setPending(false);
                        return updatedItems;
                    });
                },
                onFailure: () => {
                    setPending(false);
                },
            }),
        );
    }, [fetchAction, dispatch, update, currentPage, primaryId, extendedQueryParams, shouldReset]);

    useEffect(() => {
        if (hasMore && total > items?.length && currentPage < totalPages && !pending) {
            loadData();
        }
    }, [currentPage, items, items?.length, hasMore, loadData, total, totalPages, pending]);

    useEffect(() => {
        if (shouldReset && !pending) {
            loadData();
        }
    }, [loadData, pending, shouldReset]);

    const handleChange = (e) => {
        // const newValue = e.target.value;

        // Update the selected item if one is selected
        // if (newValue !== 'all' && newValue !== null) {
        //     const selectedItem = items.find((item) => item[primaryId] === newValue);
        //     if (selectedItem) {
        //         setCurrentSelectedItem(selectedItem);
        //     }
        // } else {
        //     setCurrentSelectedItem(null);
        // }

        if (search) {
            setSearch('');
        }

        onChange?.(
            e,
            type,
            items.find((item) => item[primaryId] === e.target.value),
        );
    };

    const { searchPlaceholder, allItems, selectItem, none } = i18nKeys || {};

    return (
        <RoundedSelect
            bold={true}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            value={displayValue}
            onChange={handleChange}
            size={size}
            search={items?.length > 10 || Boolean(search) || pending}
            searchPlaceholder={t(searchPlaceholder)}
            onSearchFilter={setSearch}
            customMenuProps={customMenuProps}
            setHasMore={setHasMore}
            pending={pending}
            fullWidth={fullWidth}
            disabled={disabled}
        >
            {selectAll ? (
                <MenuItem value="all">{t(allItems)}</MenuItem>
            ) : (
                <MenuItem disabled value="all">
                    <em>{t(selectItem)}</em>
                </MenuItem>
            )}
            {selectNone ? (
                <MenuItem value={null}>
                    <b>{t(none || 'common.none')}</b>
                </MenuItem>
            ) : null}
            {items?.map((item) => (
                <MenuItem key={item[primaryId]} value={item[primaryId]}>
                    {label(item)}
                </MenuItem>
            ))}
        </RoundedSelect>
    );
};

export default CustomPaginatedSelect;
